import { AlertTriangle, CheckCircle2, CircleSlash, RefreshCw, Search, ShieldCheck, UserRound, Users } from "lucide-react";
import { useEffect, useState } from "react";
import type { PortalAccessOverview, PortalRolePrincipal } from "../../shared/types";
import { getAccessOverview, searchAccessOverviewUsers } from "../services/portalService";
import "../styles/access-overview.css";

function checkedDate(value: string) {
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function RoleAssignments({ assignments, inherited }: {
  assignments: PortalAccessOverview["directAssignments"]; inherited?: boolean;
}) {
  return <section className="overview-role-section">
    <h4>{inherited ? <Users size={16} /> : <UserRound size={16} />}
      {inherited ? "Group-inherited roles" : "Direct assignments"}<span>{assignments.length}</span></h4>
    {assignments.length ? <ul className="overview-role-list">{assignments.map(assignment => <li key={assignment.id}>
      <strong>{assignment.roleValue || "Default application access"}</strong>
      <span>{inherited ? assignment.principalDisplayName : "Assigned directly to this user"}</span>
      {assignment.roleEnabled === false && <small className="overview-caution">Role definition disabled; assignment retained</small>}
      {assignment.createdDateTime && <small>Granted {checkedDate(assignment.createdDateTime)}</small>}
    </li>)}</ul> : <p className="overview-muted">{inherited ? "No group-inherited app roles." : "No direct app assignments."}</p>}
  </section>;
}

export function AccessOverviewPanel({ refreshSignal = 0, selection }: { refreshSignal?: number; selection?: { id: string } }) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<PortalRolePrincipal[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string>();
  const [selectedId, setSelectedId] = useState<string>();
  const [snapshot, setSnapshot] = useState<PortalAccessOverview>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [retry, setRetry] = useState(0);

  useEffect(() => { if (selection) setSelectedId(selection.id); }, [selection]);

  useEffect(() => {
    let active = true;
    setUsers([]); setSearchError(undefined);
    if (query.trim().length < 2) { setSearching(false); return; }
    setSearching(true);
    const timer = window.setTimeout(() => {
      searchAccessOverviewUsers(query.trim()).then(result => { if (active) setUsers(result.principals); })
        .catch(() => { if (active) setSearchError("Directory search is unavailable."); })
        .finally(() => { if (active) setSearching(false); });
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [query, retry, refreshSignal]);

  useEffect(() => {
    let active = true;
    setSnapshot(undefined); setError(undefined);
    if (!selectedId) return;
    setLoading(true);
    getAccessOverview(selectedId).then(result => { if (active) setSnapshot(result); })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : "Access could not be verified."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedId, retry, refreshSignal]);

  return <section className="access-overview" aria-label="Access Overview">
    <aside className="overview-directory">
      <h3>Directory users</h3>
      <div className="search-field">
        <Search size={16} aria-hidden="true" />
        <input aria-label="Find directory user" placeholder="Name or email" type="search" maxLength={200}
          value={query} onChange={event => setQuery(event.target.value)} />
      </div>
      <div className="overview-search-meta" aria-live="polite">
        {searching ? "Searching..." : query.trim().length >= 2 ? `${users.length} results` : "Entra directory"}
        <button className="ghost-button overview-icon" title="Refresh directory results" aria-label="Refresh directory results"
          disabled={searching || query.trim().length < 2} onClick={() => setRetry(value => value + 1)}><RefreshCw size={15} /></button>
      </div>
      {searchError && <p className="overview-caution" role="alert">{searchError}</p>}
      <div className="overview-user-results">
        {users.map(user => <button type="button" key={user.id} className={`overview-user${selectedId === user.id ? " selected" : ""}`}
          aria-pressed={selectedId === user.id} onClick={() => setSelectedId(user.id)}>
          <UserRound size={17} aria-hidden="true" /><span><strong>{user.displayName}</strong>
            <small>{user.mail || user.userPrincipalName}</small></span>
        </button>)}
        {!searching && !searchError && query.trim().length >= 2 && !users.length && <p className="overview-muted">No matching users.</p>}
      </div>
    </aside>
    <div className="overview-detail" aria-busy={loading}>
      {loading ? <div className="overview-state" role="status"><RefreshCw size={24} /><h3>Checking access</h3></div>
        : error ? <div className="overview-state" role="alert"><AlertTriangle size={24} /><h3>Access not verified</h3><p>{error}</p>
          <button className="ghost-button" onClick={() => setRetry(value => value + 1)}><RefreshCw size={16} />Retry</button></div>
          : !snapshot ? <div className="overview-state"><ShieldCheck size={30} /><h3>No user selected</h3></div>
            : <>
              <header className="overview-identity">
                <div><span className="overview-eyebrow">Access Overview</span><h3>{snapshot.user.displayName}</h3>
                  <p>{snapshot.user.email || snapshot.user.userPrincipalName}</p></div>
                <div className="overview-checked"><span>Checked {checkedDate(snapshot.checkedAtUtc)}</span>
                  <button className="ghost-button overview-icon" aria-label="Refresh selected user access" title="Refresh selected user access"
                    onClick={() => setRetry(value => value + 1)}><RefreshCw size={16} /></button></div>
              </header>
              <dl className="overview-facts">
                <div><dt>Entra account</dt><dd className={snapshot.user.accountEnabled ? "overview-allowed" : "overview-caution"}>
                  {snapshot.user.accountEnabled ? "Enabled" : "Sign-in blocked"}</dd></div>
                <div><dt>User type</dt><dd>{snapshot.user.userType}</dd></div>
                <div><dt>Portal policy</dt><dd>{snapshot.internal ? "Internal" : "External"} / {snapshot.effectiveStatus}</dd></div>
                <div><dt>Stored profile</dt><dd>{snapshot.profileStatus || "No profile"}</dd></div>
              </dl>
              <div className="overview-role-columns">
                <RoleAssignments assignments={snapshot.directAssignments} />
                <RoleAssignments assignments={snapshot.inheritedAssignments} inherited />
              </div>
              <section className="overview-menu-section">
                <h4>Menu access <small>{snapshot.menus.filter(menu => menu.allowed).length} / {snapshot.menus.length} allowed</small></h4>
                <div className="overview-table-wrap"><table className="overview-menu-table">
                  <thead><tr><th>Menu</th><th>Access</th><th>Reason</th></tr></thead>
                  <tbody>{snapshot.menus.map(menu => <tr key={menu.id}>
                    <th scope="row">{menu.label}</th><td><span className={menu.allowed ? "overview-result overview-allowed" : "overview-result overview-muted"}>
                      {menu.allowed ? <CheckCircle2 size={15} /> : <CircleSlash size={15} />}
                      {menu.allowed ? "Allowed" : menu.policyAllowed ? "Sign-in blocked" : "Not allowed"}</span></td>
                    <td><ul>{menu.reasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul></td>
                  </tr>)}</tbody>
                </table></div>
              </section>
              <section className="overview-stations">
                <h4>Station policy <small>{snapshot.stationScope === "all" ? "All catalog stations" : snapshot.stationScope === "assigned" ? "Assigned stations" : "No station access"}</small></h4>
                {!!snapshot.internalReasons.length && <ul className="overview-reasons">{snapshot.internalReasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>}
                {snapshot.stations.length > 0 && <details><summary>{snapshot.stations.length} stations</summary><ul className="overview-station-list">
                  {snapshot.stations.map(station => <li key={station.stationSk}><span>{station.stationName || station.businessUnitName || station.stationSk}</span>
                    <small>{station.stationSk} / {station.accessRole}</small></li>)}
                </ul></details>}
              </section>
              <footer className="overview-notes">
                <dl><div><dt>Application</dt><dd>{snapshot.application.displayName}</dd></div>
                  <div><dt>Assignment required</dt><dd>{snapshot.application.assignmentRequired ? "Yes" : "No"}</dd></div>
                  <div><dt>Object ID</dt><dd>{snapshot.user.id}</dd></div></dl>
                <ul>{snapshot.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
              </footer>
            </>}
    </div>
  </section>;
}
